import React from 'react';
import { Box, Breadcrumbs, Typography, Link } from '@mui/material';
import { NavigateNext, Home } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';

interface BreadcrumbItem {
  label: string;
  path?: string;
}

interface BreadcrumbProps {
  items?: BreadcrumbItem[];
}

const Breadcrumb: React.FC<BreadcrumbProps> = ({ items = [] }) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Generate breadcrumb items based on current path if none provided
  const generateBreadcrumbs = (): BreadcrumbItem[] => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const breadcrumbs: BreadcrumbItem[] = [];

    // Always start with Home
    breadcrumbs.push({ label: 'Home', path: '/' });

    // Generate breadcrumbs based on path segments
    let currentPath = '';
    pathSegments.forEach((segment, index) => {
      currentPath += `/${segment}`;
      
      // Convert segment to readable label
      let label = segment;
      switch (segment) {
        case 'courses':
          label = 'Courses';
          break;
        case 'profile':
          label = 'Profile';
          break;
        case 'purchases':
          label = 'My Purchases';
          break;
        case 'my-courses':
          label = 'My Courses';
          break;
        case 'login':
          label = 'Login';
          break;
        default:
          // For course detail pages, try to get course name from URL or use generic label
          if (pathSegments[index - 1] === 'courses' && segment !== 'courses') {
            label = 'Course Details';
          } else {
            // Capitalize first letter and replace hyphens with spaces
            label = segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' ');
          }
      }

      // Don't make the last item clickable (current page)
      const isLast = index === pathSegments.length - 1;
      breadcrumbs.push({
        label,
        path: isLast ? undefined : currentPath
      });
    });

    return breadcrumbs;
  };

  const breadcrumbItems = items.length > 0 ? items : generateBreadcrumbs();

  const handleClick = (path: string) => {
    navigate(path);
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Breadcrumbs
        separator={<NavigateNext fontSize="small" />}
        aria-label="breadcrumb"
        sx={{
          '& .MuiBreadcrumbs-separator': {
            color: 'text.secondary',
          },
        }}
      >
        {breadcrumbItems.map((item, index) => {
          const isLast = index === breadcrumbItems.length - 1;
          const isHome = item.label === 'Home';

          if (isLast) {
            return (
              <Typography
                key={index}
                color="text.primary"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  fontWeight: 600,
                  fontSize: '0.875rem',
                }}
              >
                {isHome && <Home sx={{ fontSize: '1rem' }} />}
                {item.label}
              </Typography>
            );
          }

          return (
            <Link
              key={index}
              component="button"
              variant="body2"
              onClick={() => item.path && handleClick(item.path)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                color: 'text.secondary',
                textDecoration: 'none',
                cursor: 'pointer',
                fontSize: '0.875rem',
                '&:hover': {
                  color: '#10b981',
                  textDecoration: 'underline',
                },
                '&:focus': {
                  outline: 'none',
                  color: '#10b981',
                },
              }}
            >
              {isHome && <Home sx={{ fontSize: '1rem' }} />}
              {item.label}
            </Link>
          );
        })}
      </Breadcrumbs>
    </Box>
  );
};

export default Breadcrumb;
